import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, CheckCircle, Mail, MessageSquare, FileText, Info, Send, X, Plus, AlertTriangle } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState({
    brevo_api_key: '',
    interakt_api_key: '',
    sender_email: '',
    sender_name: '',
    email_subject: '',
    email_body: '',
    whatsapp_template_name: '',
    whatsapp_header_type: 'document',
    whatsapp_body_variables: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [showInteraktKey, setShowInteraktKey] = useState(false);
  
  // Test email state
  const [testEmails, setTestEmails] = useState(['']);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Test WhatsApp state
  const [testWhatsAppNumber, setTestWhatsAppNumber] = useState('');
  const [sendingWhatsAppTest, setSendingWhatsAppTest] = useState(false);

  // Available template variables for email
  const templateVariables = [
    { variable: '{{attendee_name}}', description: 'Attendee full name', key: 'attendee_name' },
    { variable: '{{ticket_id}}', description: 'Unique ticket ID', key: 'ticket_id' },
    { variable: '{{event_name}}', description: 'Event name', key: 'event_name' },
    { variable: '{{event_date}}', description: 'Event date(s)', key: 'event_date' },
    { variable: '{{event_location}}', description: 'Event venue/location', key: 'event_location' },
    { variable: '{{category_name}}', description: 'Attendee category', key: 'category_name' },
    { variable: '{{club_name}}', description: 'Club/Organization name', key: 'club_name' },
  ];

  const defaultEmailSubject = 'Your Badge for {{event_name}}';
  
  const defaultEmailBody = `Hello {{attendee_name}}!

Your event badge is ready! Please find it attached to this email.

Event Details:
- Event: {{event_name}}
- Date: {{event_date}}
- Location: {{event_location}}
- Your Ticket ID: {{ticket_id}}
- Category: {{category_name}}

Important: Please download and print your badge, or save it on your mobile device.

We look forward to seeing you at the event!`;

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      setSettings({
        brevo_api_key: '',
        interakt_api_key: '',
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || '',
        email_subject: response.data.email_subject || defaultEmailSubject,
        email_body: response.data.email_body || defaultEmailBody,
        whatsapp_template_name: response.data.whatsapp_template_name || '',
        whatsapp_header_type: response.data.whatsapp_header_type || 'document',
        whatsapp_body_variables: response.data.whatsapp_body_variables || [],
        brevo_api_key_set: response.data.brevo_api_key_set,
        interakt_api_key_set: response.data.interakt_api_key_set
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [defaultEmailSubject, defaultEmailBody]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (settings.brevo_api_key) updateData.brevo_api_key = settings.brevo_api_key;
      if (settings.interakt_api_key) updateData.interakt_api_key = settings.interakt_api_key;
      if (settings.sender_email) updateData.sender_email = settings.sender_email;
      if (settings.sender_name) updateData.sender_name = settings.sender_name;
      if (settings.email_subject) updateData.email_subject = settings.email_subject;
      if (settings.email_body) updateData.email_body = settings.email_body;
      if (settings.whatsapp_template_name) updateData.whatsapp_template_name = settings.whatsapp_template_name;
      if (settings.whatsapp_header_type) updateData.whatsapp_header_type = settings.whatsapp_header_type;
      if (settings.whatsapp_body_variables) updateData.whatsapp_body_variables = settings.whatsapp_body_variables;
      
      await api.put('/settings', updateData);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('email_body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = settings.email_body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setSettings({ ...settings, email_body: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setSettings({ ...settings, email_body: settings.email_body + variable });
    }
    toast.success(`Inserted ${variable}`);
  };

  const resetToDefault = () => {
    setSettings({
      ...settings,
      email_subject: defaultEmailSubject,
      email_body: defaultEmailBody
    });
    toast.info('Template reset to default');
  };

  // Test email functions
  const addTestEmail = () => {
    setTestEmails([...testEmails, '']);
  };

  const removeTestEmail = (index) => {
    if (testEmails.length > 1) {
      setTestEmails(testEmails.filter((_, i) => i !== index));
    }
  };

  const updateTestEmail = (index, value) => {
    const updated = [...testEmails];
    updated[index] = value;
    setTestEmails(updated);
  };

  const sendTestEmails = async () => {
    const validEmails = testEmails.filter(email => email.trim() && email.includes('@'));
    if (validEmails.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }

    setSendingTest(true);
    try {
      const response = await api.post('/settings/test-email', {
        emails: validEmails
      });
      
      if (response.data.success) {
        toast.success(`Test email sent to ${validEmails.length} recipient(s)!`);
      } else {
        toast.error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // WhatsApp variable mapping
  const addWhatsAppVariable = () => {
    setSettings({
      ...settings,
      whatsapp_body_variables: [...settings.whatsapp_body_variables, { position: settings.whatsapp_body_variables.length + 1, field: '' }]
    });
  };

  const updateWhatsAppVariable = (index, field) => {
    const updated = [...settings.whatsapp_body_variables];
    updated[index] = { ...updated[index], field };
    setSettings({ ...settings, whatsapp_body_variables: updated });
  };

  const removeWhatsAppVariable = (index) => {
    const updated = settings.whatsapp_body_variables.filter((_, i) => i !== index);
    // Re-index positions
    const reindexed = updated.map((v, i) => ({ ...v, position: i + 1 }));
    setSettings({ ...settings, whatsapp_body_variables: reindexed });
  };

  const sendTestWhatsApp = async () => {
    if (!testWhatsAppNumber || testWhatsAppNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setSendingWhatsAppTest(true);
    try {
      const response = await api.post('/settings/test-whatsapp', {
        phone_number: testWhatsAppNumber
      });
      
      if (response.data.success) {
        toast.success('Test WhatsApp message sent!');
      } else {
        toast.error(response.data.error || 'Failed to send test message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test WhatsApp');
    } finally {
      setSendingWhatsAppTest(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Settings">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings">
      <Tabs defaultValue="api" className="max-w-4xl">
        <TabsList className="mb-4">
          <TabsTrigger value="api">API Configuration</TabsTrigger>
          <TabsTrigger value="email-template">Email Template</TabsTrigger>
          <TabsTrigger value="whatsapp-template">WhatsApp Template</TabsTrigger>
        </TabsList>

        <TabsContent value="api" className="space-y-6">
          {/* Email Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" /> Email Configuration (Brevo)
              </CardTitle>
              <CardDescription>
                Configure Brevo (Sendinblue) API for sending badge emails
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="brevo_api_key">Brevo API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="brevo_api_key"
                      type={showBrevoKey ? 'text' : 'password'}
                      value={settings.brevo_api_key}
                      onChange={(e) => setSettings({...settings, brevo_api_key: e.target.value})}
                      placeholder={settings.brevo_api_key_set ? '•••••••• (already set)' : 'Enter API key'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowBrevoKey(!showBrevoKey)}
                    >
                      {showBrevoKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {settings.brevo_api_key_set && (
                    <CheckCircle className="h-5 w-5 text-green-500 self-center" />
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Get your API key from <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Brevo Dashboard</a>
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_email">Sender Email</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={settings.sender_email}
                    onChange={(e) => setSettings({...settings, sender_email: e.target.value})}
                    placeholder="noreply@yourdomain.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_name">Sender Name</Label>
                  <Input
                    id="sender_name"
                    value={settings.sender_name}
                    onChange={(e) => setSettings({...settings, sender_name: e.target.value})}
                    placeholder="Event Team"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" /> WhatsApp Configuration (Interakt)
              </CardTitle>
              <CardDescription>
                Configure Interakt API for sending WhatsApp messages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="interakt_api_key">Interakt API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="interakt_api_key"
                      type={showInteraktKey ? 'text' : 'password'}
                      value={settings.interakt_api_key}
                      onChange={(e) => setSettings({...settings, interakt_api_key: e.target.value})}
                      placeholder={settings.interakt_api_key_set ? '•••••••• (already set)' : 'Enter API key'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowInteraktKey(!showInteraktKey)}
                    >
                      {showInteraktKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {settings.interakt_api_key_set && (
                    <CheckCircle className="h-5 w-5 text-green-500 self-center" />
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Get your API key from <a href="https://app.interakt.ai/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Interakt Dashboard</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Settings'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="email-template" className="space-y-6">
          {/* Email Template Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> Email Template
              </CardTitle>
              <CardDescription>
                Customize the email that is sent with the badge attachment. The badge PDF is automatically attached.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email_subject">Email Subject</Label>
                <Input
                  id="email_subject"
                  value={settings.email_subject}
                  onChange={(e) => setSettings({...settings, email_subject: e.target.value})}
                  placeholder="Your Badge for {{event_name}}"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="email_body">Email Message</Label>
                  <Button variant="outline" size="sm" onClick={resetToDefault}>
                    Reset to Default
                  </Button>
                </div>
                <Textarea
                  id="email_body"
                  value={settings.email_body}
                  onChange={(e) => setSettings({...settings, email_body: e.target.value})}
                  placeholder="Enter your email message..."
                  className="min-h-[300px] text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Available Variables */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" /> Available Variables
              </CardTitle>
              <CardDescription>
                Click on a variable to insert it into your email template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {templateVariables.map((item) => (
                  <button
                    key={item.variable}
                    onClick={() => insertVariable(item.variable)}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full border border-blue-200 transition-colors"
                    title={item.description}
                  >
                    <span className="font-mono">{item.variable}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                These variables will be replaced with actual attendee and event information when the email is sent.
              </p>
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" /> Send Test Email
              </CardTitle>
              <CardDescription>
                Test your email template by sending it to specific email addresses.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Recipient Email Addresses</Label>
                {testEmails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateTestEmail(index, e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1"
                    />
                    {testEmails.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeTestEmail(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTestEmail}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Another Email
                </Button>
              </div>

              <Button 
                onClick={sendTestEmails} 
                disabled={sendingTest || !settings.brevo_api_key_set}
                className="w-full"
              >
                {sendingTest ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send Test Email</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Template'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="whatsapp-template" className="space-y-6">
          {/* Important Notice */}
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" /> Important: WhatsApp Template Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="text-amber-700 text-sm space-y-2">
              <p><strong>Before you can send badges via WhatsApp, you must:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Create a template in your <a href="https://app.interakt.ai/" target="_blank" rel="noopener noreferrer" className="text-amber-900 underline">Interakt Dashboard</a></li>
                <li>Get the template approved by WhatsApp (usually takes 24-48 hours)</li>
                <li>Enter the <strong>exact template name</strong> below (case-sensitive)</li>
                <li>Map the template variables in the correct order</li>
              </ol>
              <p className="mt-3">The badge PDF will be sent as a document attachment in the header.</p>
            </CardContent>
          </Card>

          {/* WhatsApp Template Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" /> WhatsApp Template Configuration
              </CardTitle>
              <CardDescription>
                Configure your approved WhatsApp template for sending badges
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="whatsapp_template_name">Template Name (from Interakt)</Label>
                <Input
                  id="whatsapp_template_name"
                  value={settings.whatsapp_template_name}
                  onChange={(e) => setSettings({...settings, whatsapp_template_name: e.target.value})}
                  placeholder="e.g., event_badge_notification"
                />
                <p className="text-xs text-gray-500">
                  Enter the exact template name as it appears in your Interakt dashboard (case-sensitive)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Header Type</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="header_type"
                      value="document"
                      checked={settings.whatsapp_header_type === 'document'}
                      onChange={(e) => setSettings({...settings, whatsapp_header_type: e.target.value})}
                      className="text-primary"
                    />
                    <span className="text-sm">Document (PDF Badge)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="header_type"
                      value="image"
                      checked={settings.whatsapp_header_type === 'image'}
                      onChange={(e) => setSettings({...settings, whatsapp_header_type: e.target.value})}
                      className="text-primary"
                    />
                    <span className="text-sm">Image</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Variable Mapping */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" /> Template Variable Mapping
              </CardTitle>
              <CardDescription>
                Map your WhatsApp template variables to attendee data fields. Add variables in the same order as your template.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm mb-4">
                <p className="font-medium text-blue-800 mb-1">ℹ️ How it works:</p>
                <ul className="text-blue-700 text-xs list-disc pl-4 space-y-1">
                  <li>Template must be pre-approved in your WhatsApp Business Account</li>
                  <li>Template name must match exactly (case-sensitive)</li>
                  <li>Number of variables must match your template exactly</li>
                  <li>Badge PDF will be automatically hosted and sent as document attachment</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p className="font-medium mb-2">Example: If your template body is:</p>
                <p className="text-gray-600 italic">&quot;Hello {'{{1}}'}, your badge for {'{{2}}'} is attached. Your ticket ID is {'{{3}}'}.&quot;</p>
                <p className="mt-2 text-gray-500">Map: 1 → attendee_name, 2 → event_name, 3 → ticket_id</p>
              </div>

              <div className="space-y-2">
                <Label>Body Variables (in order)</Label>
                {settings.whatsapp_body_variables.map((variable, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <span className="w-16 text-sm text-gray-500">{`{{${index + 1}}}`}</span>
                    <select
                      value={variable.field}
                      onChange={(e) => updateWhatsAppVariable(index, e.target.value)}
                      className="flex-1 border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select field...</option>
                      <option value="attendee_name">Attendee Name</option>
                      <option value="ticket_id">Ticket ID</option>
                      <option value="event_name">Event Name</option>
                      <option value="event_date">Event Date</option>
                      <option value="event_location">Event Location</option>
                      <option value="category_name">Category</option>
                      <option value="club_name">Club/Organization</option>
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeWhatsAppVariable(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addWhatsAppVariable}
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Variable
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test WhatsApp */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" /> Send Test WhatsApp
              </CardTitle>
              <CardDescription>
                Test your WhatsApp template by sending a test message with sample data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Phone Number (with country code)</Label>
                <Input
                  type="tel"
                  value={testWhatsAppNumber}
                  onChange={(e) => setTestWhatsAppNumber(e.target.value)}
                  placeholder="919876543210"
                />
                <p className="text-xs text-gray-500">Enter number without + or spaces (e.g., 919876543210 for India)</p>
              </div>

              <Button 
                onClick={sendTestWhatsApp} 
                disabled={sendingWhatsAppTest || !settings.interakt_api_key_set || !settings.whatsapp_template_name}
                className="w-full"
              >
                {sendingWhatsAppTest ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><MessageSquare className="h-4 w-4 mr-2" /> Send Test WhatsApp</>
                )}
              </Button>

              {!settings.interakt_api_key_set && (
                <p className="text-xs text-red-500 text-center">
                  Please configure your Interakt API key first in the API Configuration tab.
                </p>
              )}
              {settings.interakt_api_key_set && !settings.whatsapp_template_name && (
                <p className="text-xs text-red-500 text-center">
                  Please enter your WhatsApp template name above.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save WhatsApp Settings'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default Settings;
